import { useEffect, useMemo } from 'react';
import { Box, Button, Chip, DroppableArea, IconButton, Paper, Stack } from '@common-components';
import { Add, Close } from '@icons';
import { BoxTemplateLabels } from 'enums';
import { usePagination } from 'hooks';
import { useReFetchBoxItems, useSearchBoxItems } from 'hooks/api/box';
import { messages } from 'i18n';
import { softShadow } from 'themes';
import Filter, { FilterButtonTypes } from 'broker/components/common/Filter';
import TablePagination from 'broker/components/common/TablePagination';
import ContainedFileViewerModalWrapper from 'broker/components/FilesExplorer/components/Dialogs/ContainedFileViewerModalWrapper';
import FileOperationOverlay from 'broker/components/FilesExplorer/components/FileOperationOverlay';
import { defaultFileTypesFilter } from 'broker/components/FilesExplorer/config';
import {
  useFileActionsCallbacks,
  useFileOperationOverlay,
  useFilesFilter,
  useFilterAndSortFiles,
  useShowUploader,
} from 'broker/components/FilesExplorer/hooks';
import {
  componentId,
  ExtendedBoxItem,
  FileExplorerFilter,
  FilesExplorerProps,
} from 'broker/components/FilesExplorer/types';
import ToolboxItemHeader from 'broker/components/ToolboxItemHeader';
import FilesListSection from './FilesListSection';
import FilesUploadSection from './FilesUploaderSection';

export default function Content(filesExplorerProps: FilesExplorerProps) {
  const {
    hideFilesConfig,
    folderId,
    title,
    subtitle,
    filter,
    visibleFileIds,
    selectionMode,
    selectedFiles,
    isLoadingAdditionalFileMetadata,
    fileListFooterContent,
    viewOnlyMode,
    labelsMetadata,
    onFileSelected,
    setSelectedFiles,
    syncSelectedFiles,
    initialSelectedFilePage,
    submissionId,
    getFileSecondaryActions,
    getPrimaryActionComponent,
    highlightFileTypes,
    autoLabel,
    alwaysShowUploader,
    onFileUploadCompleted,
  } = filesExplorerProps;
  const { reFetchFileItems } = useReFetchBoxItems();
  const { showSimpleOverlay, hideOverlay } = useFileOperationOverlay();

  const labelsListByMetadata: FileExplorerFilter = useMemo(
    () => ({
      fileType: defaultFileTypesFilter,
      marketId:
        labelsMetadata
          ?.find((label) => label.metadataFieldName === BoxTemplateLabels.MarketId)
          ?.options.map((option) => ({
            value: option.value,
            label: option.label,
          })) ?? [],
    }),
    [labelsMetadata],
  );

  const { items, isLoading: boxItemsLoading } = useSearchBoxItems({
    filter: { chunkSize: 20, folderId },
    // we don't want to cache the files explorer as it should reflect the latest state of the files
    // including file changes by our lambdas
    queryOptions: { staleTime: 0 },
  });
  const isLoading = boxItemsLoading || isLoadingAdditionalFileMetadata;

  useEffect(
    () => {
      if (selectedFiles && setSelectedFiles) {
        syncSelectedFiles?.(items);
      }
    },
    // This effect syncs the selected files box items, we only want it to run if items were re-fetched (for example file was renamed, label was changed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  );

  const availableFiles = useFilterAndSortFiles({
    allItems: items,
    hideFilesConfig,
    visibleFileIds,
    ...filesExplorerProps,
  });
  const { page, setPage, rowsPerPage, setRowsPerPage, paginationReset } = usePagination();

  const {
    filteredFiles,
    handleFileFilterChange,
    fileFilters,
    isAllFileFilters,
    handleAllFileFilterSelected,
    search,
    setSearch,
    handleUncheckedFileFilter,
  } = useFilesFilter({
    files: availableFiles,
    initFilter: filter,
    filterFileTypes: labelsListByMetadata[BoxTemplateLabels.FileType]!,
    filterMarkets: labelsListByMetadata[BoxTemplateLabels.MarketId]!,
    paginationReset,
  });

  const filesSlice = filteredFiles.slice(page * rowsPerPage - rowsPerPage, page * rowsPerPage);
  const doesFolderHasFiles = !isLoading && availableFiles.length > 0;
  const isFolderEmpty = !isLoading && availableFiles.length === 0;

  const { uploaderShown, showUploader, setUploaderVisible, onCloseUploader, filesUploaderRef } = useShowUploader({
    isFolderEmpty,
  });
  const afterFileOperation = async () => {
    showSimpleOverlay(messages.fileExplorer.loadingFiles);
    paginationReset();
    await reFetchFileItems();
    hideOverlay();
  };

  const silentAfterFileOperation = async () => {
    await reFetchFileItems();
  };

  const { onLabelClicked } = useFileActionsCallbacks({
    afterFileOperation,
    submissionId,
  });
  const onLabelUpdate = async (
    extendedBoxItem: ExtendedBoxItem,
    labelFieldName: BoxTemplateLabels,
    selectedLabel?: string,
  ) => {
    await onLabelClicked(extendedBoxItem, labelFieldName, selectedLabel);
    silentAfterFileOperation();
  };

  const mainActionArea = uploaderShown ? (
    <IconButton color="secondary" size="small" icon={Close} onClick={onCloseUploader} />
  ) : (
    <Button
      onClick={showUploader}
      startIcon={<Add fontSize="small" />}
      variant="contained"
      color="secondary"
      size="small"
    >
      {messages.fileExplorer.iconTooltips.addFiles}
    </Button>
  );

  return (
    <DroppableArea disabled={viewOnlyMode} setIsDraggedOver={setUploaderVisible}>
      <Stack
        id={componentId}
        className={componentId}
        sx={{ height: 1, maxHeight: 1, color: 'common.black', overflow: 'hidden', position: 'relative' }}
      >
        <FileOperationOverlay />
        <ToolboxItemHeader
          title={title || messages.fileExplorer.title}
          subtitle={
            selectionMode ? `${subtitle || messages.fileExplorer.filesSelected(selectedFiles?.length ?? 0)}` : subtitle
          }
          mainActionArea={viewOnlyMode ? undefined : mainActionArea}
          searchProps={{ search, setSearch, searchPlaceholder: messages.fileExplorer.searchPlaceHolder }}
          hideSearchSection={uploaderShown}
          arrangingArea={
            <Filter
              buttonType={FilterButtonTypes.FilterIcon}
              filterIconTooltipText={messages.fileExplorer.iconTooltips.filterByLabels}
              filters={fileFilters}
              handleFilterChange={handleFileFilterChange}
              isAllFilters={isAllFileFilters}
              handleAllSelected={handleAllFileFilterSelected}
              search
            />
          }
        />
        {fileFilters.some((label) => label.checked) && (
          <Stack direction="row" sx={{ p: 2, pt: 0, gap: 0.5, flexWrap: 'wrap', maxHeight: 64, overflowY: 'auto' }}>
            {fileFilters
              .filter((label) => label.checked)
              .map((label) => (
                <Box key={label.key}>
                  <Chip
                    rounded
                    label={label.label}
                    onClick={() => handleUncheckedFileFilter(label.key)}
                    size="medium"
                    endIcon={Close}
                    variant="contained"
                    color="secondary"
                  />
                </Box>
              ))}
          </Stack>
        )}

        <Stack flex={1} gap={0} justifyContent="start" overflow="hidden" sx={{ scrollbarWidth: 'none' }}>
          <FilesListSection
            afterFileOperation={afterFileOperation}
            isLoading={isLoading}
            doesFolderHasFiles={doesFolderHasFiles}
            uploaderShown={uploaderShown}
            filesSlice={filesSlice}
            search={search}
            silentAfterFileOperation={silentAfterFileOperation}
            selectionMode={selectionMode}
            onFileSelected={onFileSelected}
            selectedFiles={selectedFiles}
            getFileSecondaryActions={getFileSecondaryActions}
            getPrimaryActionComponent={getPrimaryActionComponent}
            highlightFileTypes={highlightFileTypes}
            viewOnlyMode={viewOnlyMode}
            labelsMetadata={labelsMetadata}
            onLabelUpdate={onLabelUpdate}
            setSelectedFiles={setSelectedFiles}
            submissionId={submissionId}
          />
          <FilesUploadSection
            afterFileOperation={afterFileOperation}
            autoLabel={autoLabel}
            ownFilter={labelsListByMetadata}
            filesUploaderRef={filesUploaderRef}
            showUploader={showUploader}
            selectionMode={selectionMode}
            setSelectedFiles={setSelectedFiles}
            selectedFiles={selectedFiles}
            isLoading={isLoading}
            folderId={folderId}
            uploaderShown={uploaderShown}
            alwaysShowUploader={alwaysShowUploader}
            onCloseUploader={onCloseUploader}
            onFileUploadCompleted={onFileUploadCompleted}
            viewOnlyMode={viewOnlyMode}
          />
        </Stack>
        <Stack borderTop={1} borderColor="grey.300">
          {doesFolderHasFiles && !uploaderShown && (
            <TablePagination
              hideNumbers
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              overrideContainerStyle={{ background: 'transparent' }}
              from={page * rowsPerPage - rowsPerPage + 1}
              to={page * rowsPerPage}
              outOf={filteredFiles.length}
              handleChange={(value: number) => {
                setPage(value);
              }}
              count={Math.ceil(filteredFiles.length / rowsPerPage)}
              page={page}
            />
          )}
          {fileListFooterContent && (
            <Stack
              p={2}
              component={Paper}
              boxShadow={softShadow}
              direction="row"
              justifyContent="center"
              alignItems="center"
              bgcolor="transparent"
            >
              {fileListFooterContent}
            </Stack>
          )}
        </Stack>
      </Stack>
      <ContainedFileViewerModalWrapper
        selectionMode={selectionMode}
        viewOnlyMode={viewOnlyMode}
        items={items}
        afterFileOperation={afterFileOperation}
        onFileSelected={onFileSelected}
        selectedFiles={selectedFiles}
        labelsMetadata={labelsMetadata}
        onLabelUpdate={onLabelUpdate}
        submissionId={submissionId}
        initialSelectedFilePage={initialSelectedFilePage}
      />
    </DroppableArea>
  );
}
